import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Plus, Minus, Boxes } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/bio/StatusBadge";
import { EmptyState } from "@/components/bio/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { formatUSD } from "@/lib/format";

export default function InventoryView() {
    const [items, setItems] = useState(null);
    const [q, setQ] = useState("");
    const [lowOnly, setLowOnly] = useState(false);

    const load = useCallback(() => {
        const params = {};
        if (q) params.q = q;
        if (lowOnly) params.low_only = true;
        api.get("/inventory", { params }).then(({ data }) => setItems(data));
    }, [q, lowOnly]);

    useEffect(() => { load(); }, [load]);

    const adjust = async (p, delta) => {
        const next = Math.max(0, (p.stock || 0) + delta);
        const { data } = await api.patch(`/inventory/${p.id}`, { stock: next });
        setItems((its) => its.map((x) => (x.id === p.id ? data : x)));
        if (data.status === "low") toast.warning(`Stock crítico: ${data.name}`);
    };

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Inventario y productos</h1>
                <p className="text-bio-text-secondary mt-1 text-sm">Controla el stock y detecta productos con stock crítico.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-bio-text-muted" />
                    <Input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto…" className="h-11 pl-10 rounded-bio bg-white border-[1.5px]" />
                </div>
                <label className="flex items-center gap-2.5 text-sm font-medium">
                    <Switch data-testid="inv-low-only" checked={lowOnly} onCheckedChange={setLowOnly} /> Solo stock crítico
                </label>
            </div>

            {!items ? (
                <Skeleton className="h-64 w-full rounded-bio-card" />
            ) : items.length === 0 ? (
                <div className="bg-white rounded-bio-card border border-bio-border-subtle"><EmptyState icon={Boxes} title="Sin productos" description="No hay productos que coincidan." /></div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden md:block rounded-[12px] border border-bio-border-subtle overflow-hidden bg-white">
                        <table className="w-full text-left">
                            <thead className="bg-bio-bg border-b border-bio-border-subtle text-xs font-bold uppercase tracking-wider text-bio-text-secondary">
                                <tr><th className="px-5 py-3">Producto</th><th className="px-5 py-3">Categoría</th><th className="px-5 py-3">Precio</th><th className="px-5 py-3">Stock</th><th className="px-5 py-3">Estado</th><th className="px-5 py-3 text-right">Ajustar</th></tr>
                            </thead>
                            <tbody>
                                {items.map((p) => (
                                    <tr key={p.id} data-testid={`inv-row-${p.id}`} className="border-b border-bio-border-subtle last:border-0 hover:bg-bio-bg-alt/50">
                                        <td className="px-5 py-3"><div className="flex items-center gap-3"><img src={p.image} alt="" className="h-9 w-9 rounded-bio object-cover" /><span className="font-semibold text-sm">{p.name}</span></div></td>
                                        <td className="px-5 py-3 text-sm text-bio-text-secondary">{p.category}</td>
                                        <td className="px-5 py-3 text-sm">{formatUSD(p.price)}</td>
                                        <td className="px-5 py-3 font-bold">{p.stock} <span className="text-xs font-normal text-bio-text-muted">/ mín {p.min_stock}</span></td>
                                        <td className="px-5 py-3"><StatusBadge status={p.status === "low" ? "low_stock" : "online"} /></td>
                                        <td className="px-5 py-3">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <button data-testid={`inv-dec-${p.id}`} onClick={() => adjust(p, -5)} className="h-8 w-8 rounded-bio border border-bio-border flex items-center justify-center hover:bg-bio-bg"><Minus className="h-3.5 w-3.5" /></button>
                                                <button data-testid={`inv-inc-${p.id}`} onClick={() => adjust(p, 5)} className="h-8 w-8 rounded-bio border border-bio-border flex items-center justify-center hover:bg-bio-bg"><Plus className="h-3.5 w-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {items.map((p) => (
                            <div key={p.id} className="bg-white rounded-bio-card border border-bio-border-subtle p-4">
                                <div className="flex items-center gap-3">
                                    <img src={p.image} alt="" className="h-11 w-11 rounded-bio object-cover" />
                                    <div className="flex-1 min-w-0"><p className="font-semibold text-sm truncate">{p.name}</p><p className="text-xs text-bio-text-muted">{p.category} · {formatUSD(p.price)}</p></div>
                                    <StatusBadge status={p.status === "low" ? "low_stock" : "online"} />
                                </div>
                                <div className="mt-3 flex items-center justify-between">
                                    <span className="font-bold">{p.stock} <span className="text-xs font-normal text-bio-text-muted">/ mín {p.min_stock}</span></span>
                                    <div className="flex gap-1.5">
                                        <button onClick={() => adjust(p, -5)} className="h-8 w-8 rounded-bio border border-bio-border flex items-center justify-center"><Minus className="h-3.5 w-3.5" /></button>
                                        <button onClick={() => adjust(p, 5)} className="h-8 w-8 rounded-bio border border-bio-border flex items-center justify-center"><Plus className="h-3.5 w-3.5" /></button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
